import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  Eye, 
  CheckCircle, 
  XCircle, 
  Clock, 
  DollarSign, 
  User, 
  Calendar,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  RefreshCw,
  Download,
  MoreHorizontal
} from 'lucide-react';
import { adminService } from '../services/adminService';
import toast from 'react-hot-toast';

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPaymentMode, setSelectedPaymentMode] = useState('all');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusDialogLoading, setStatusDialogLoading] = useState(false);
  const [statusDialogMessage, setStatusDialogMessage] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: null,
    paymentId: null,
    message: '',
  });

  // Fetch payments on component mount
  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const data = await adminService.getPayments();
      
      if (Array.isArray(data)) {
        setPayments(data);
        setError(null);
        console.log('Payments loaded:', data.length);
      } else {
        console.error('Unexpected data format:', data);
        setPayments([]);
        setError('Invalid data format received from server');
        toast.error('Invalid data format received');
      }
    } catch (err) {
      console.error('Error fetching payments:', err);
      setPayments([]);
      setError(err.message);
      toast.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  // Mock data for demonstration
  const mockPayments = [
    {
      id: 1,
      order_id: 'ORDER_1703123456789_123',
      user_name: 'John Doe',
      user_role: 'Brand',
      user_email: 'john@example.com',
      user_mobile: '+919999999999',
      amount: 1000,
      status: 'success',
      payment_mode: 'UPI',
      coins_granted: 1000,
      created_at: '2024-01-20T10:00:00.000Z',
      updated_at: '2024-01-20T10:05:00.000Z',
      cf_payment_id: 'CF123456789',
      utr: 'UTR123456789'
    },
    {
      id: 2,
      order_id: 'ORDER_1703123456788_124',
      user_name: 'Jane Smith',
      user_role: 'Influencer',
      user_email: 'jane@example.com',
      user_mobile: '+919888888888',
      amount: 500,
      status: 'pending',
      payment_mode: 'UPI',
      coins_granted: 0,
      created_at: '2024-01-19T15:30:00.000Z',
      updated_at: '2024-01-19T15:30:00.000Z',
      cf_payment_id: null,
      utr: null
    },
    {
      id: 3,
      order_id: 'ORDER_1703123456787_125',
      user_name: 'Mike Johnson',
      user_role: 'SMA',
      user_email: 'mike@example.com',
      user_mobile: '+919777777777',
      amount: 2000,
      status: 'failed',
      payment_mode: 'UPI',
      coins_granted: 0,
      created_at: '2024-01-18T12:00:00.000Z',
      updated_at: '2024-01-18T12:10:00.000Z',
      cf_payment_id: 'CF987654321',
      utr: null
    }
  ];

  // Use real data if available, otherwise fallback to mock
  const displayPayments = error || !Array.isArray(payments) || payments.length === 0 ? mockPayments : payments;
  
  const filteredPayments = displayPayments.filter(payment => {
    const matchesSearch = (
      (payment.order_id && payment.order_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (payment.user_name && payment.user_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (payment.user_email && payment.user_email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (payment.user_mobile && payment.user_mobile.includes(searchTerm))
    );
    const matchesStatus = selectedStatus === 'all' || payment.status === selectedStatus;
    const matchesPaymentMode = selectedPaymentMode === 'all' || payment.payment_mode === selectedPaymentMode;
    
    let matchesDate = true;
    if (dateRange.from && dateRange.to) {
      const paymentDate = new Date(payment.created_at);
      const fromDate = new Date(dateRange.from);
      const toDate = new Date(dateRange.to);
      matchesDate = paymentDate >= fromDate && paymentDate <= toDate;
    }
    
    return matchesSearch && matchesStatus && matchesPaymentMode && matchesDate;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      success: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
      processing: 'bg-blue-100 text-blue-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig[status] || 'bg-gray-100 text-gray-800'}`}>
        {status || 'unknown'}
      </span>
    );
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'processing':
        return <RefreshCw className="h-4 w-4 text-blue-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getPaymentModeBadge = (mode) => {
    const modeConfig = {
      UPI: 'bg-purple-100 text-purple-800',
      CARD: 'bg-blue-100 text-blue-800',
      NETBANKING: 'bg-green-100 text-green-800',
      WALLET: 'bg-orange-100 text-orange-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${modeConfig[mode] || 'bg-gray-100 text-gray-800'}`}>
        {mode || 'Unknown'}
      </span>
    );
  };

  const getTotalStats = () => {
    const totalPayments = displayPayments.length;
    const successfulPayments = displayPayments.filter(p => p.status === 'success').length;
    const pendingPayments = displayPayments.filter(p => p.status === 'pending').length;
    const failedPayments = displayPayments.filter(p => p.status === 'failed').length;
    const totalAmount = displayPayments
      .filter(p => p.status === 'success')
      .reduce((sum, p) => sum + Number(p.amount), 0);
    const totalCoins = displayPayments
      .filter(p => p.status === 'success')
      .reduce((sum, p) => sum + Number(p.coins_granted), 0);
    
    return { 
      totalPayments, 
      successfulPayments, 
      pendingPayments, 
      failedPayments, 
      totalAmount, 
      totalCoins 
    };
  };

  const stats = getTotalStats();

  const handleApprovePayment = async (paymentId) => {
    try {
      setProcessingPayment(true);
      await adminService.approvePayment(paymentId);
      toast.success('Payment approved successfully!');
      fetchPayments(); // Refresh the list
    } catch (error) {
      toast.error('Failed to approve payment: ' + error.message);
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleRejectPayment = async (paymentId) => {
    try {
      setProcessingPayment(true);
      await adminService.rejectPayment(paymentId); // <-- use this, not updatePaymentStatus
      toast.success('Payment rejected successfully!');
      fetchPayments(); // Refresh the list
    } catch (error) {
      toast.error('Failed to reject payment: ' + error.message);
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleCheckStatus = async (paymentId) => {
    setStatusDialogOpen(true);
    setStatusDialogLoading(true);
    setStatusDialogMessage('');
    try {
      const result = await adminService.checkPaymentStatus(paymentId);
      setStatusDialogLoading(false);
      setStatusDialogMessage(
        result.payment_status
          ? `Payment status: ${result.payment_status}`
          : result.message
          ? result.message
          : 'Payment status not available'
      );
      fetchPayments();
    } catch (error) {
      setStatusDialogLoading(false);
      setStatusDialogMessage(
        error?.response?.data?.error ||
        error?.message ||
        'Failed to check payment status'
      );
    }
  };

  const openPaymentModal = (payment) => {
    setSelectedPayment(payment);
    setShowPaymentModal(true);
  };

  const closePaymentModal = () => {
    setSelectedPayment(null);
    setShowPaymentModal(false);
  };

  const exportPayments = () => {
    const csvContent = [
      ['Order ID', 'User', 'Amount', 'Status', 'Payment Mode', 'Created Date', 'UTR'],
      ...filteredPayments.map(p => [
        p.order_id,
        p.user_name,
        p.amount,
        p.status,
        p.payment_mode,
        new Date(p.created_at).toLocaleDateString(),
        p.utr || 'N/A'
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payments_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Custom confirm dialog handlers
  const openConfirmDialog = (action, paymentId, message) => {
    setConfirmDialog({ open: true, action, paymentId, message });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, action: null, paymentId: null, message: '' });
  };

  const handleConfirmAction = async () => {
    if (confirmDialog.action === 'approve') {
      await handleApprovePayment(confirmDialog.paymentId);
    } else if (confirmDialog.action === 'reject') {
      await handleRejectPayment(confirmDialog.paymentId);
    }
    closeConfirmDialog();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payment Management</h1>
          <p className="text-gray-600">
            {error ? 'Showing demo data (API connection failed)' : 'Monitor and manage all payment transactions'}
          </p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={exportPayments}
            className="btn-secondary flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
          <button 
            onClick={fetchPayments}
            disabled={loading}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Payments</p>
              <p className="text-2xl font-bold text-blue-600">{stats.totalPayments}</p>
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Successful</p>
              <p className="text-2xl font-bold text-green-600">{stats.successfulPayments}</p>
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pendingPayments}</p>
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Failed</p>
              <p className="text-2xl font-bold text-red-600">{stats.failedPayments}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <TrendingUp className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Amount</p>
              <p className="text-2xl font-bold text-purple-600">₹{stats.totalAmount.toLocaleString()}</p>
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-orange-100">
              <User className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Coins Granted</p>
              <p className="text-2xl font-bold text-orange-600">{stats.totalCoins.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by order ID, user name, email, or mobile..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          
          <div>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="input-field"
            >
              <option value="all">All Status</option>
              <option value="success">Success</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="processing">Processing</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          <div>
            <select
              value={selectedPaymentMode}
              onChange={(e) => setSelectedPaymentMode(e.target.value)}
              className="input-field"
            >
              <option value="all">All Modes</option>
              <option value="UPI">UPI</option>
              <option value="CARD">Card</option>
              <option value="NETBANKING">Net Banking</option>
              <option value="WALLET">Wallet</option>
            </select>
          </div>
          
          <div className="flex gap-2">
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
              className="input-field"
            />
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
              className="input-field"
            />
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="card p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payments...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="card p-6 mb-6">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <p className="text-gray-600 mb-4">Showing demo data below. Click retry to attempt fetching real data again.</p>
            <button onClick={fetchPayments} className="btn-primary">
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Payments Table */}
      {!loading && (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment Mode
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPayments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <DollarSign className="h-5 w-5 text-primary-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {payment.order_id}
                          </div>
                          <div className="text-sm text-gray-500">
                            {payment.cf_payment_id || 'No CF ID'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {payment.user_name?.charAt(0) || 'U'}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {payment.user_name || 'Unknown User'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {payment.user_role || 'Unknown Role'}
                          </div>
                          <div className="text-xs text-gray-400">
                            {payment.user_mobile || 'No mobile'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <span className="font-medium">₹{payment.amount?.toLocaleString() || '0'}</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {payment.coins_granted || 0} coins
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(payment.status)}
                        <span className="ml-2">{getStatusBadge(payment.status)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPaymentModeBadge(payment.payment_mode)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        {payment.created_at ? new Date(payment.created_at).toLocaleDateString() : 'N/A'}
                      </div>
                      <div className="text-xs text-gray-400">
                        {payment.utr ? `UTR: ${payment.utr}` : 'No UTR'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button 
                          onClick={() => openPaymentModal(payment)}
                          className="text-primary-600 hover:text-primary-900"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        
                        {payment.status === 'pending' && (
                          <>
                            <button 
                              onClick={() => openConfirmDialog('approve', payment.id, 'Are you sure you want to approve this payment?')}
                              disabled={processingPayment}
                              className="text-green-600 hover:text-green-900"
                              title="Approve Payment"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            <button 
                              onClick={() => openConfirmDialog('reject', payment.id, 'Are you sure you want to reject this payment?')}
                              disabled={processingPayment}
                              className="text-red-600 hover:text-red-900"
                              title="Reject Payment"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        
                        <button 
                          onClick={() => handleCheckStatus(payment.id)}
                          disabled={processingPayment}
                          className="text-blue-600 hover:text-blue-900"
                          title="Check Status"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button className="btn-secondary">Previous</button>
              <button className="btn-secondary">Next</button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">1</span> to <span className="font-medium">{filteredPayments.length}</span> of{' '}
                  <span className="font-medium">{filteredPayments.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button className="btn-secondary rounded-l-md">Previous</button>
                  <button className="btn-secondary rounded-r-md">Next</button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredPayments.length === 0 && (
        <div className="card p-12 text-center">
          <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
            <DollarSign className="h-12 w-12" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No payments found</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || selectedStatus !== 'all' || selectedPaymentMode !== 'all' || dateRange.from || dateRange.to
              ? 'Try adjusting your search or filter criteria.'
              : 'No payment transactions have been recorded yet.'
            }
          </p>
        </div>
      )}

      {/* Payment Details Modal */}
      {showPaymentModal && selectedPayment && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Payment Details</h3>
                <button
                  onClick={closePaymentModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Order ID</label>
                    <p className="text-sm text-gray-900">{selectedPayment.order_id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <div className="flex items-center mt-1">
                      {getStatusIcon(selectedPayment.status)}
                      <span className="ml-2">{getStatusBadge(selectedPayment.status)}</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Amount</label>
                    <p className="text-sm text-gray-900">₹{selectedPayment.amount?.toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Coins Granted</label>
                    <p className="text-sm text-gray-900">{selectedPayment.coins_granted || 0}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Payment Mode</label>
                    <p className="text-sm text-gray-900">{selectedPayment.payment_mode}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">CF Payment ID</label>
                    <p className="text-sm text-gray-900">{selectedPayment.cf_payment_id || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">UTR</label>
                    <p className="text-sm text-gray-900">{selectedPayment.utr || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Created Date</label>
                    <p className="text-sm text-gray-900">
                      {selectedPayment.created_at ? new Date(selectedPayment.created_at).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">User Information</label>
                  <div className="mt-1 p-3 bg-gray-50 rounded-md">
                    <p className="text-sm text-gray-900"><strong>Name:</strong> {selectedPayment.user_name}</p>
                    <p className="text-sm text-gray-900"><strong>Role:</strong> {selectedPayment.user_role}</p>
                    <p className="text-sm text-gray-900"><strong>Email:</strong> {selectedPayment.user_email}</p>
                    <p className="text-sm text-gray-900"><strong>Mobile:</strong> {selectedPayment.user_mobile}</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end space-x-3">
                {selectedPayment.status === 'pending' && (
                  <>
                    <button
                      onClick={() => {
                        handleApprovePayment(selectedPayment.id);
                        closePaymentModal();
                      }}
                      disabled={processingPayment}
                      className="btn-primary"
                    >
                      {processingPayment ? 'Processing...' : 'Approve Payment'}
                    </button>
                    <button
                      onClick={() => {
                        handleRejectPayment(selectedPayment.id);
                        closePaymentModal();
                      }}
                      disabled={processingPayment}
                      className="btn-danger"
                    >
                      {processingPayment ? 'Processing...' : 'Reject Payment'}
                    </button>
                  </>
                )}
                <button
                  onClick={closePaymentModal}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Dialog */}
      {statusDialogOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-gray-900 bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-8 flex flex-col items-center min-w-[300px] min-h-[150px]">
            {statusDialogLoading ? (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
                <div className="text-lg text-gray-700">Checking payment status...</div>
              </>
            ) : (
              <div className="text-xl font-semibold text-center text-gray-900">{statusDialogMessage}</div>
            )}
            <button
              className="mt-6 btn-secondary px-6 py-2"
              onClick={() => setStatusDialogOpen(false)}
              disabled={statusDialogLoading}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Custom Confirm Dialog */}
      {confirmDialog.open && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-gray-900 bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-8 flex flex-col items-center min-w-[300px]">
            <div className="text-lg font-semibold text-gray-900 mb-4">{confirmDialog.message}</div>
            <div className="flex gap-4">
              <button
                className="btn-primary px-6 py-2"
                onClick={handleConfirmAction}
                disabled={processingPayment}
              >
                Yes
              </button>
              <button
                className="btn-secondary px-6 py-2"
                onClick={closeConfirmDialog}
                disabled={processingPayment}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;
